import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL || '/api';

function BookingForm({ request, equipment, offer, onClose, userTelegramId }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [pricePerHour, setPricePerHour] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);

  useEffect(() => {
    if (equipment && equipment.price_per_hour) setPricePerHour(parseFloat(equipment.price_per_hour));
    else if (request && request.price_per_hour) setPricePerHour(parseFloat(request.price_per_hour));
    else if (offer && offer.price) setPricePerHour(parseFloat(offer.price));
  }, [equipment, request, offer]);

  useEffect(() => {
    if (startDate && endDate && pricePerHour > 0) {
      const hours = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60));
      const base = hours * pricePerHour;
      const fee = Math.round(base * 0.07 * 100) / 100;
      setServiceFee(fee);
      setTotalPrice(base + fee); // итоговая сумма к оплате
    } else {
      setServiceFee(0);
      setTotalPrice(0);
    }
  }, [startDate, endDate, pricePerHour]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const bookingData = {
        customer_id: parseInt(userTelegramId),
        owner_id: equipment?.owner_id || request?.user_id,
        start_date: startDate,
        end_date: endDate,
        total_price: totalPrice,
        customer_phone: customerPhone,
        customer_address: customerAddress,
        comment
      };
      if (equipment) bookingData.equipment_id = equipment.id;
      else if (request && request.price_per_hour) bookingData.request_id = request.id;

      const bookingResponse = await fetch(API_URL + '/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify(bookingData)
      });
      const bookingResult = await bookingResponse.json();
      if (!bookingResponse.ok) throw new Error(bookingResult.error || 'Ошибка бронирования');

      const paymentResponse = await fetch(API_URL + '/create-payment-hold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify({ bookingId: bookingResult.booking.id, amount: totalPrice, description: 'Бронирование #' + bookingResult.booking.id })
      });
      const paymentData = await paymentResponse.json();
      if (!paymentResponse.ok) throw new Error(paymentData.error || 'Ошибка платежа');

      window.location.href = paymentData.paymentUrl;
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const hours = startDate && endDate ? Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60)) : 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-industrial-bg2 rounded-2xl max-w-3xl w-full border border-slate-700 shadow-large my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
          <h2 className="text-xl font-black text-white m-0 uppercase tracking-tight">📅 Бронирование</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl bg-transparent border-none cursor-pointer">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Инфо о технике */}
          {(equipment || (request && request.price_per_hour) || offer) && (
            <div className="mb-6 p-4 bg-industrial-bg rounded-xl border border-gold/30 flex justify-between items-center">
              <div>
                <div className="text-xs text-slate-400 uppercase font-bold mb-1">Техника</div>
                <div className="text-white font-bold">{equipment?.model || (request?.details?.substring(0, 40)) || 'Аренда'}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400 uppercase font-bold mb-1">Цена</div>
                <div className="text-gold text-xl font-black">{pricePerHour} ₽<span className="text-sm">/час</span></div>
              </div>
            </div>
          )}

          {/* 2 колонки */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Дата и время начала</label>
              <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} required className="w-full" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Дата и время окончания</label>
              <input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="w-full" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Ваш телефон</label>
              <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+7XXXXXXXXXX" required className="w-full" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Адрес работ</label>
              <input type="text" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} required className="w-full" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Комментарий</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows="2" placeholder="Дополнительные пожелания" className="w-full" />
          </div>

          {/* Итого */}
          <div className="mb-6 p-5 bg-gold/10 border border-gold/40 rounded-xl">
            <div className="text-xs text-gold uppercase font-bold mb-3">Итого к оплате</div>
            <div className="flex justify-between text-sm text-slate-300 mb-2">
              <span>{hours} ч × {pricePerHour} ₽</span>
              <span>{(totalPrice - serviceFee).toLocaleString('ru-RU')} ₽</span>
            </div>
            <div className="flex justify-between text-sm text-slate-400 mb-3 pb-3 border-b border-gold/20">
              <span>Сервисный сбор</span>
              <span>+ {serviceFee.toLocaleString('ru-RU')} ₽</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gold uppercase font-bold">К оплате</span>
              <span className="text-3xl font-black text-gold">{totalPrice.toLocaleString('ru-RU')} ₽</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={loading} className="btn btn-primary flex-1">
              {loading ? 'Обработка...' : '💳 ЗАБРОНИРОВАТЬ И ОПЛАТИТЬ'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-outline px-8">Отмена</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BookingForm;
